import { useState } from "react"
import { DeleteIcon } from "components/ui/icons/userActions/DeleteIcon"
import { ButtonOptions } from "components/ui/buttons/ButtonOptions/ButtonOptions"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import style from "./SidebarOptionsMenu.module.scss"

const { menu, optionsButton, deleteItem } = style

type Props = {
	/** Raises the caller's delete confirmation. The menu closes itself first. */
	onDelete: () => void
	/** Names the ⋯ button for screen readers — "Object actions", "Elevation callout actions", … */
	ariaLabel: string
}

/**
 * The right-sidebar header's ⋯ → Delete menu. Extracted from `FurnitureHeader` (REQ-296) when the
 * ecall sidebar gained the same affordance (REQ-297) so the two cannot drift — the trigger chip,
 * the red Delete item and the Escape handling all live here.
 */
export const SidebarOptionsMenu = ({ onDelete, ariaLabel }: Props) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	return (
		<div
			className={menu}
			onKeyDown={(event) => {
				// Escape belongs to the open menu. The editor's Escape hotkey is bound on `window` in
				// the bubble phase, so stopping propagation here — during the React tree's own bubble,
				// the same escape hatch focused text fields use — keeps it from closing the whole
				// sidebar (hotkeys/useEditorHotkeys → closeRightSidebar) when the user only meant to
				// dismiss the dropdown.
				if (event.key !== "Escape" || !isMenuOpen) return
				event.stopPropagation()
				setIsMenuOpen(false)
			}}
		>
			{/* `renderInPlace` keeps the menu inside the shadow root — the default AntD portal targets
			    document.body, which is outside it and would drop the injected styles. */}
			<Dropdown
				renderInPlace
				// AntD opens on hover by default, which would pop the menu open whenever the cursor
				// merely crossed the header's right edge.
				trigger={["click"]}
				placement="bottomRight"
				open={isMenuOpen}
				onOpenChange={setIsMenuOpen}
				items={[
					{
						key: "delete",
						label: (
							<span className={deleteItem}>
								<DeleteIcon variant="red" />
								Delete
							</span>
						),
						// Dismiss explicitly: with `open` controlled, the menu would otherwise stay up
						// behind the confirmation modal it just raised.
						onClick: () => {
							setIsMenuOpen(false)
							onDelete()
						},
					},
				]}
			>
				<span>
					<ButtonOptions className={optionsButton} ariaLabel={ariaLabel} />
				</span>
			</Dropdown>
		</div>
	)
}
